<template>
  <div v-if="showList" class="retailers-list-wrapper">
    <h3 v-if="!!listTitle" class="title">{{ listTitle }}</h3>

    <div class="retailers-list">
      <loading-icon v-if="loading"/>
      <div v-else-if="hasNoResults" class="empty-response">no results found</div>
      <div v-else v-for="(item, index) in availableItems" class="item-wrapper" :key="index">
        <label :class="['item', `item-${item.id}`]">
          <input
            class="hide"
            type="checkbox"
            :id="item.id"
            :value="item.id"
            v-model="itemIds"
            @change="clickItem(item)">
          <div class="item-info">
            <span class="link">{{ item.detail }}</span>
            <span>{{ item.subDetail }}</span>
          </div>
          <div class="checkmark">
            <div class="check"/>
          </div>
        </label>
      </div>
    </div>

    <div v-if="hasSelectAll" class="select-all">
      <span class="link" @click="selectAllItems">select all</span>
      <div>{{abbrNum(totalConsumers, 1)}} consumers</div>
      <div class="delete-link" @click="$emit('showClearAllModal')">clear selected</div>
    </div>

    <pagination
      v-if="!loading && totalPages && totalPages > 1"
      :currentPage="currentPage"
      :totalPageCount="totalPages"
      showArrows
      @changePage="changePage">
    </pagination>
  </div>

</template>

<script>
  import LoadingIcon from '../Shared/LoadingIcon'
  import Pagination from '../Shared/Pagination'
  import { NumberFormatters } from '../mixins/NumberFormatters.js'
  import { mapGetters } from 'vuex'
  import { brandsAPI } from '../../services/api/brands'
  import { calculateTotalPagesUtil } from '../../utilities'

  export default {
    props: {
      currentAdvertiserId: [Number, String],
      dataBreakdown: Function,
      errorTerm: {
        type: String,
        default: 'list'
      },
      hasSelectAll: Boolean,
      listKey: String,
      listTitle: String,
      listUrl: String,
      searchText: String,
      selectAll: Boolean,
      selectedItemIds: Array,
      showEmptyList: Boolean,
      useFiltersAndSort: Boolean,
    },

    name: 'RetailersList',

    mixins: [NumberFormatters],

    components: {
      LoadingIcon,
      Pagination,
    },

    data() {
      return {
        availableItems: null,
        currentPage: 1,
        hasOriginalItems: false,
        itemIds: [],
        loading: true,
        params: {
          region: 'q[region_in][]',
          search: 'q[name_cont]',
          sort: 'name asc'
        },
        totalConsumers: null,
        totalPages: null,
      }
    },

    created() {
      if (this.useFiltersAndSort) {
        this.$store.commit('SET_AD_SORT', { adSort: `q[s][]=${this.params.sort}` })
        this.$store.commit('SET_AD_REGION', { regionSort: '[]' })
      }
      this.getList(true)
      this.itemIds = this.selectedItemIds
    },

    computed: {
      queries() {
        let query = ''

        if (this.useFiltersAndSort) {
          if (this.getAdRegion?.length) this.getAdRegion.forEach(region => { query += `&${this.params.region}=${region}` })
          if (this.getAdSort) query += `&${this.getAdSort}`
        }

        if (this.searchText != null) query += `&${this.params.search}=${this.searchText}`
        return query
      },

      hasNoResults() {
        return !this.availableItems?.length
      },

      showList() {
        return this.showEmptyList ? true : this.hasOriginalItems
      },

      ...mapGetters([
        'getAdSort',
        'getAdRegion'
      ])
    },

    methods: {
      clickItem(selectedItem){
        this.setSelectedItems({selectedItem})
      },

      changePage(page) {
        this.currentPage = page
        this.getList()
      },

      configureItems(data, items, checkAvailableItems) {
        this.availableItems = !!items?.length ? items.map(this.dataBreakdown) : []
        if (checkAvailableItems) this.hasOriginalItems = !!this.availableItems?.length
        const pagination = data?.pagination || data?.meta
        this.totalPages = calculateTotalPagesUtil(pagination?.total_elements, pagination?.per_page)
        this.totalConsumers = data?.meta?.total_recipient_size
      },

      async getList(checkAvailableItems = false) {
        try {
          this.loading = true
          const data = await brandsAPI.getList({
            brandId: this.currentAdvertiserId,
            urlString: this.listUrl,
            extraParams: this.queries,
            page: this.currentPage
          })

          if (!!data[this.listKey]) {
            this.configureItems(data, data[this.listKey], checkAvailableItems)
            this.$emit('setRetailerErrors')
          } else {
            if (this.totalPages) { this.totalPages = null }
            this.$emit('setRetailerErrors', `there was an error loading ${this.errorTerm}, please refresh or contact support`)
          }
        } catch (error) {
          if (this.totalPages) { this.totalPages = null }
          this.$emit('setRetailerErrors', error)
        } finally { this.loading = false }
      },

      selectAllItems() {
        this.setSelectedItems({selectAll: true})
      },

      setSelectedItems({selectedItem = null, selectAll = false}) {
        this.$emit('setSelectedRetailerIds', selectedItem, this.itemIds, {
          selectAll,
          query: this.queries
        })
      }
    },

    watch: {
      selectedItemIds() {
        this.itemIds = this.selectedItemIds
      },

      searchText() {
        this.changePage(1)
      }
    }
  }
</script>

<style lang="sass" scoped>
@import '../../assets/sass/colors'

.retailers-list-wrapper
  width: 100%

  .title
    border-bottom: 1px solid $primary-color
    line-height: 30px
    margin-bottom: 20px
    width: max-content

  .retailers-list
    align-content: inherit
    display: flex
    flex-wrap: wrap
    overflow: scroll
    flex-direction: column

    .empty-response
      max-width: 80%;

  .pagination-section
    margin-left: auto
    margin-right: auto

  ::-webkit-scrollbar
    -webkit-appearance: none
    width: 7px

  ::-webkit-scrollbar-thumb
    border-radius: 4px
    background-color: rgba(0,0,0,.5)
    box-shadow: 0 0 1px rgba(255,255,255,.5)

  .item-wrapper
    flex-basis: 49%
    margin-bottom: 2rem
    @media only screen and (max-width: 40em)
      flex-basis: 100%

  .item
    align-items: center
    color: $title-color
    cursor: pointer
    display: flex
    font-size: 1.25rem
    margin-right: .5rem
    text-align: left
    .checkmark
      min-width: 22px

  .item input:checked ~ .checkmark
    display: flex

  .merchant-wrapper .checkmark
    float: right
    position: relative
    right: 4em
    top: -15px

  .merchant-wrapper .checkmark
    float: right
    position: relative
    right: 4em
    top: -15px

  .item input:checked ~ .item-info > .link
    color: $title-color

  .item-info
    align-items: flex-start
    display: flex
    flex-direction: column
    .link
      margin: 0 auto .5rem 0
      overflow-wrap: anywhere

  .select-all
    text-align: left

    .link
      font-size: 16px

  .delete-link
    margin-top: 20px
</style>